import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosClient from '../axios/api.js';

// Thunks backing the `DeletedVendor` route. Only the deleted-vendor list and
// its restore/hard-delete actions live here — the material-code lookups,
// duplicate/similarity checks, progress polling, template URLs and the
// add/edit wizard (`/api/modular/*`) are shared with `VendorDatabase` and are
// imported from `vendorSlice` by the page.

const asMessage = (error, fallback) =>
  error.response?.data?.error ||
  error.response?.data?.message ||
  error.message ||
  fallback;

// ------------------------------------------------------ deleted vendor list

export const fetchDeletedVendors = createAsyncThunk(
  'deletedVendors/fetchDeletedVendors',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get('/api/deleted-vendordata');
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Error 404 please try again later'));
    }
  }
);

export const searchDeletedVendors = createAsyncThunk(
  'deletedVendors/searchDeletedVendors',
  async (params, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get('/api/vendor-search', { params: { ...params, status: 'Deleted' } });
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Error searching deleted vendors.'));
    }
  }
);

// Same endpoint as VendorDatabase's filter, but a distinct action type so the
// two routes' lists never overwrite each other.
export const applyDeletedVendorFilter = createAsyncThunk(
  'deletedVendors/applyDeletedVendorFilter',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post('/api/applyvendorfilter', payload);
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to apply vendor filter'));
    }
  }
);

export const hardDeleteVendor = createAsyncThunk(
  'deletedVendors/hardDeleteVendor',
  async (vendorName, { rejectWithValue }) => {
    try {
      await axiosClient.delete(`/api/vendor-hard/${vendorName}`);
      return vendorName;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Delete company error'));
    }
  }
);

export const restoreVendor = createAsyncThunk(
  'deletedVendors/restoreVendor',
  async (vendorName, { rejectWithValue }) => {
    try {
      await axiosClient.put(`/api/vendor-restore/${vendorName}`);
      return vendorName;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to restore vendor'));
    }
  }
);







const initialState = {
  deletedVendors: [],
  loading: true,
  error: null,
  searchResults: [],
  searchLoading: false,
  searchTotal: 0,
};

const deletedVendorSlice = createSlice({
  name: 'deletedVendors',
  initialState,
  reducers: {
    setDeletedVendors: (state, action) => {
      state.deletedVendors = action.payload;
    },
    setDeletedVendorLoading: (state, action) => {
      state.loading = action.payload;
    },
    clearDeletedVendorError: (state) => {
      state.error = null;
    },
    resetDeletedVendorState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDeletedVendors.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDeletedVendors.fulfilled, (state, action) => {
        state.loading = false;
        state.deletedVendors = action.payload;
      })
      .addCase(fetchDeletedVendors.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(searchDeletedVendors.pending, (state) => {
        state.searchLoading = true;
        state.error = null;
      })
      .addCase(searchDeletedVendors.fulfilled, (state, action) => {
        state.searchLoading = false;
        state.searchResults = action.payload.data;
        state.searchTotal = action.payload.pagination.total;
      })
      .addCase(searchDeletedVendors.rejected, (state, action) => {
        state.searchLoading = false;
        state.error = action.payload;
        state.searchResults = [];
        state.searchTotal = 0;
      })

      .addCase(applyDeletedVendorFilter.pending, (state) => {
        state.loading = true;
      })
      .addCase(applyDeletedVendorFilter.fulfilled, (state, action) => {
        state.loading = false;
        state.deletedVendors = action.payload;
      })
      .addCase(applyDeletedVendorFilter.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  setDeletedVendors,
  setDeletedVendorLoading,
  clearDeletedVendorError,
  resetDeletedVendorState,
} = deletedVendorSlice.actions;

export default deletedVendorSlice.reducer;
